import { useEffect, useState } from 'react';
import { mensagemDoErro } from '../../api/erros.js';
import {
  dispararCargaInicial,
  ETAPAS_DA_CARGA,
  obterCargaInicial,
} from '../../api/sincronizacao.js';
import type { CargaDeLotes } from '../../api/lotes.js';
import type { CargaInicial } from '../../api/sincronizacao.js';
import { useConsulta } from '../../api/useConsulta.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import type { TomDoAviso } from '../../componentes/AvisoDaTela.js';
import { Botao } from '../../componentes/Botao.js';
import { Cartao } from '../../componentes/Cartao.js';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { FalhaDeCarregamento } from '../../componentes/FalhaDeCarregamento.js';
import { CarregarLotes, useCargaDeLotes } from './EtapaDeLotes.js';
import type { AcaoDaCarga } from './situacao.js';
import {
  acaoDaCarga,
  estadoDaEtapa,
  formatarDuracao,
  processadosNaEtapa,
  rotuloDoEstadoDaEtapa,
  rotuloDoStatusDaCarga,
  tomDoEstadoDaEtapa,
  tomDoStatusDaCarga,
} from './situacao.js';

/**
 * De quanto em quanto tempo a tela pergunta de novo enquanto a carga roda. A
 * consulta é uma linha só no servidor; cinco segundos mostram o contador
 * andando sem martelar o middleware.
 */
export const INTERVALO_DA_CARGA_MS = 5_000;

const NUMERO = new Intl.NumberFormat('pt-BR');

const ROTULO_DA_ACAO: Readonly<Record<AcaoDaCarga, string>> = {
  iniciar: 'Iniciar carga',
  retomar: 'Retomar',
};

type Aviso = { readonly tom: TomDoAviso; readonly texto: string };

type Props = {
  /** Só o Administrador dispara (`sincronizacao.gerenciar`). */
  readonly podeDisparar: boolean;
  /** Chamado a cada nova leitura enquanto roda — o resumo acompanha junto. */
  readonly aoAvancar: () => void;
};

/**
 * Carga inicial de categorias, produtos, saldos e lotes (r1-sync).
 *
 * Roda UMA vez por armazém, em segundo plano, na ordem Categorias → Produtos
 * → Saldos → Lotes. Enquanto está em andamento, o cartão pergunta de novo sozinho. A
 * que falhou (ou ficou parada porque o servidor reiniciou) se retoma do
 * último lote gravado.
 */
export function CartaoCargaInicial({ podeDisparar, aoAvancar }: Props) {
  const consulta = useConsulta(obterCargaInicial);
  const { recarregar } = consulta;
  const [confirmando, definirConfirmando] = useState<AcaoDaCarga | null>(null);
  const [agindo, definirAgindo] = useState(false);
  const [erroDoDialogo, definirErroDoDialogo] = useState<string | null>(null);
  const [aviso, definirAviso] = useState<Aviso | null>(null);

  const emAndamento = consulta.dados?.status === 'EmAndamento';
  const lotes = useCargaDeLotes(consulta.dados?.status === 'Concluida', aoAvancar);

  useEffect(() => {
    if (!emAndamento) {
      return;
    }

    const relogio = setInterval(() => {
      recarregar();
      aoAvancar();
    }, INTERVALO_DA_CARGA_MS);

    return () => {
      clearInterval(relogio);
    };
  }, [emAndamento, recarregar, aoAvancar]);

  function disparar() {
    definirAgindo(true);
    definirErroDoDialogo(null);

    dispararCargaInicial()
      .then((disparo) => {
        definirAgindo(false);
        definirConfirmando(null);
        definirAviso({
          tom: 'ok',
          texto: disparo.retomada
            ? 'Carga inicial retomada do último lote gravado. O progresso aparece aqui.'
            : 'Carga inicial iniciada. O progresso aparece aqui.',
        });
        recarregar();
        aoAvancar();
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        definirErroDoDialogo(mensagemDoErro(falha));
      });
  }

  const carga = consulta.dados;
  const acao = carga === null || !podeDisparar ? null : acaoDaCarga(carga);

  return (
    <Cartao
      titulo="Carga inicial"
      acessorio={
        acao === null ? undefined : (
          <Botao
            variante="primario"
            altura="pequena"
            onClick={() => {
              definirAviso(null);
              definirConfirmando(acao);
            }}
          >
            {ROTULO_DA_ACAO[acao]}
          </Botao>
        )
      }
    >
      {aviso !== null && <AvisoDaTela tom={aviso.tom}>{aviso.texto}</AvisoDaTela>}

      {consulta.erro !== null ? (
        <FalhaDeCarregamento mensagem={consulta.erro} aoTentarDeNovo={recarregar} />
      ) : carga === null ? (
        <p className="sincronizacao__discreto" role="status">
          Carregando a carga inicial…
        </p>
      ) : (
        <EstadoDaCarga
          carga={carga}
          cargaDeLotes={lotes.dados}
          podeDisparar={podeDisparar}
          aoDispararLotes={lotes.recarregar}
        />
      )}

      {confirmando !== null && (
        <DialogoConfirmacao
          titulo={
            confirmando === 'iniciar' ? 'Iniciar a carga inicial?' : 'Retomar a carga inicial?'
          }
          rotuloConfirmar={ROTULO_DA_ACAO[confirmando]}
          variante="primario"
          agindo={agindo}
          aoConfirmar={disparar}
          aoCancelar={() => {
            definirConfirmando(null);
            definirErroDoDialogo(null);
          }}
        >
          {confirmando === 'iniciar'
            ? 'As categorias, os produtos, os saldos e os lotes do ERP serão lidos e gravados aqui, nesta ordem, em segundo plano. A carga roda uma vez só por armazém. '
            : 'A carga continua do último lote gravado, sem repetir o que já entrou. '}
          Saldo daqui diferente do ERP vira divergência pendente — nunca é sobrescrito. Nada é
          escrito no ERP.
          {erroDoDialogo !== null && (
            <span className="sincronizacao__erro-dialogo" role="alert">
              {erroDoDialogo}
            </span>
          )}
        </DialogoConfirmacao>
      )}
    </Cartao>
  );
}

function EstadoDaCarga({
  carga,
  cargaDeLotes,
  podeDisparar,
  aoDispararLotes,
}: {
  readonly carga: CargaInicial;
  /** `null` enquanto não respondeu (ou falhou): a linha Lotes não afirma concluída. */
  readonly cargaDeLotes: CargaDeLotes | null;
  readonly podeDisparar: boolean;
  readonly aoDispararLotes: () => void;
}) {
  const indiceDaEtapa = carga.etapaAtual === null ? -1 : ETAPAS_DA_CARGA.indexOf(carga.etapaAtual);
  const parada = carga.status === 'EmAndamento' && !carga.emExecucao;

  return (
    <div className="sincronizacao__carga">
      <p className="sincronizacao__estado">
        <ChipStatus
          tom={tomDoStatusDaCarga(carga.status)}
          rotulo={rotuloDoStatusDaCarga(carga.status)}
        />
        {indiceDaEtapa >= 0 && carga.status !== 'Concluida' && (
          <span className="sincronizacao__discreto">
            Etapa {indiceDaEtapa + 1} de {ETAPAS_DA_CARGA.length}: {carga.etapaAtual}
          </span>
        )}
        {parada && (
          <span className="sincronizacao__discreto">
            Parou de avançar (o servidor reiniciou). Retome para continuar do último lote gravado.
          </span>
        )}
      </p>

      {carga.status === 'Falhou' && carga.ultimoErro !== null && (
        <AvisoDaTela tom="erro">Por que parou: {carga.ultimoErro}</AvisoDaTela>
      )}

      <ol className="sincronizacao__etapas" aria-label="Etapas da carga inicial">
        {ETAPAS_DA_CARGA.map((etapa) => {
          const estado = estadoDaEtapa(carga, etapa, cargaDeLotes);
          const lotesPorCarregar =
            etapa === 'Lotes' &&
            carga.status === 'Concluida' &&
            (estado === 'pendente' || estado === 'falhou');
          return (
            <li key={etapa} className="sincronizacao__etapa">
              <span className="sincronizacao__etapa-nome">{etapa}</span>
              <ChipStatus
                tom={tomDoEstadoDaEtapa(estado)}
                rotulo={rotuloDoEstadoDaEtapa(estado)}
                tamanho="tabela"
              />
              <span className="sincronizacao__etapa-contador">
                {NUMERO.format(processadosNaEtapa(carga, etapa, cargaDeLotes))} processados
              </span>
              {lotesPorCarregar &&
                estado === 'falhou' &&
                cargaDeLotes !== null &&
                cargaDeLotes.ultimoErro !== null && (
                  <span className="sincronizacao__discreto">{cargaDeLotes.ultimoErro}</span>
                )}
              {lotesPorCarregar && podeDisparar && <CarregarLotes aoDisparar={aoDispararLotes} />}
            </li>
          );
        })}
      </ol>

      <dl className="sincronizacao__fatos">
        <div>
          <dt>Início</dt>
          <dd>{formatarDataHora(carga.iniciadaEm)}</dd>
        </div>
        <div>
          <dt>Fim</dt>
          <dd>{formatarDataHora(carga.concluidaEm)}</dd>
        </div>
        <div>
          <dt>Duração</dt>
          <dd>{formatarDuracao(carga.duracaoMs)}</dd>
        </div>
        <div>
          <dt>Último lote gravado</dt>
          <dd>{formatarDataHora(carga.sincronizadoEm)}</dd>
        </div>
        <div>
          <dt>Disparada por</dt>
          <dd>{carga.disparadaPor ?? '—'}</dd>
        </div>
      </dl>

      <ErrosDaCarga carga={carga} />
    </div>
  );
}

/** A lista vem cortada nos primeiros 500; o total continua contando. */
function ErrosDaCarga({ carga }: { readonly carga: CargaInicial }) {
  if (carga.registrosComErro === 0 && carga.erros.length === 0) {
    return null;
  }

  return (
    <details className="sincronizacao__erros">
      <summary>
        {NUMERO.format(carga.registrosComErro)}{' '}
        {carga.registrosComErro === 1 ? 'registro com erro' : 'registros com erro'} ou aviso
        {carga.erros.length < carga.registrosComErro &&
          ` (mostrando os ${NUMERO.format(carga.erros.length)} primeiros)`}
      </summary>
      <ul aria-label="Erros da carga inicial">
        {carga.erros.map((erro, indice) => (
          <li key={`${erro.etapa}-${String(indice)}`}>
            <strong>{erro.etapa}:</strong> {erro.mensagem}
          </li>
        ))}
      </ul>
    </details>
  );
}
